/* eslint-disable object-shorthand */
import { Box, Stack, Typography, useTheme } from '@mui/material';
import PlanComparisonComponent from 'components/PlanComparisionScreen/PlanComparisionComponent';
import PlanPageLoader from 'components/PlanPageLoader/PlanPageLoader';
import { DEFAULT_PRODUCT_TYPE } from 'configs/constants';
import { useHolidayDiscount } from 'configs/coupon';
import { useLoadingStates } from 'hooks/useLoadingStates';
import analyticsService, {
  AppEvent
} from 'infrastructure/serviceHandlers/Analytics/Analytics';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreatePurchaseId } from 'services/api/paymentsApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchAllPlans } from 'store/reducers/allPlanReducers';
import { fetchFreeTrialEligibility } from 'store/reducers/freeTrialEligibilityReducers';
import {
  fetchAllSubscription,
  setSelectedSubscriptionInfo
} from 'store/reducers/subscriptionReducer';
import {
  SUBSCRIPTION_TYPE,
  TFreeTrialState,
  TPlanList,
  TPurchasePayload,
  TSpaceState,
  TSubScriptionState
} from 'typings';
import { FREE_TRIAL_DURATION } from 'utils/constants';
import {
  APP_BUTTON_TYPES,
  isRunningInWebView,
  showAppButton
} from 'utils/webviewHelpers';

function BuySubscription() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { palette } = useTheme();

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.subscription.buySubscription'
  });

  const { deviceId } = location?.state || {
    deviceId: ''
  };
  // const [error, setError] = useState<string | null>(null);
  // const [allPlans, setAllPlans] = useState<TPlanList[]>([]);
  const [selectedCyclesPlans, setSelectedCyclesPlans] = useState<TPlanList[]>(
    []
  );
  const [isPlanFiltered, setIsPlanFiltered] = useState(false);

  const freeTrialData = analyticsService.fetchVariant('free-trial-days');
  console.log('freeTrialVariantData', freeTrialData);
  const freeTrialDays = Number(freeTrialData?.value) || FREE_TRIAL_DURATION;
  const isDefaultFreeTrialDays =
    Number(freeTrialData?.value) === FREE_TRIAL_DURATION;

  const { spaceData, isLoading: isSpaceDataLoading } =
    useAppSelector<TSpaceState>((state) => state.space);

  const { allPlans, isLoading: isAllPlansLoading } = useAppSelector(
    (state) => state.allPlans
  );

  const {
    homeSecuritySubscriptionList,
    subscriptionData,
    isLoading: isSubscriptionLoading
  } = useAppSelector<TSubScriptionState>((state) => state.subscription);

  const {
    isEligibleForFreeTrialHomeSecurity: isFreeTrial,
    isLoading: isEligibleForFreeTrialLoading
  } = useAppSelector<TFreeTrialState>((state) => state.trial);

  const isLoading = useLoadingStates([
    isSubscriptionLoading,
    isAllPlansLoading,
    isSpaceDataLoading,
    isEligibleForFreeTrialLoading
  ]);
  const { IS_HOLIDAY_DISCOUNT_ACTIVE } = useHolidayDiscount();

  const enablePromoDiscount = IS_HOLIDAY_DISCOUNT_ACTIVE;

  useEffect(() => {
    if (spaceData?.id) {
      dispatch(fetchAllPlans(spaceData?.address?.country));
      dispatch(fetchAllSubscription());
      dispatch(
        fetchFreeTrialEligibility({
          productType: DEFAULT_PRODUCT_TYPE
        })
      );
    }
  }, [spaceData]);

  useEffect(() => {
    if (isRunningInWebView()) {
      // Pairing Flow
      if (deviceId && isFreeTrial) {
        showAppButton(APP_BUTTON_TYPES.RELOAD, APP_BUTTON_TYPES.BACK);
      } else if (deviceId && !isFreeTrial) {
        showAppButton(APP_BUTTON_TYPES.RELOAD, APP_BUTTON_TYPES.BACK);
      } else {
        showAppButton(APP_BUTTON_TYPES.RELOAD, APP_BUTTON_TYPES.CLOSE);
      }
    }
  }, [deviceId, isFreeTrial]);

  const filterPlansForCycle = (isYearlyCycle: boolean) => {
    const cycleMonths = isYearlyCycle ? 12 : 1;
    const cyclePlans = allPlans.filter(
      (el: TPlanList) => el.subscription_cycle === cycleMonths
    );
    // debugger
    cyclePlans.sort(
      (
        a: { prices: { original_price: number }[] },
        b: { prices: { original_price: number }[] }
      ) => b.prices[0].original_price - a.prices[0].original_price
    );
    setSelectedCyclesPlans(cyclePlans);
    setIsPlanFiltered(true);
  };

  useEffect(() => {
    if (allPlans?.length) {
      filterPlansForCycle(true);
    }
  }, [allPlans]);

  const createPurchaseId = async (plan: TPlanList) => {
    try {
      const eligibleForFreeTrial =
        homeSecuritySubscriptionList[0]?.subscription_type ===
        SUBSCRIPTION_TYPE.PREVIEW
          ? false
          : isFreeTrial;

      const payload: TPurchasePayload = {
        country: plan?.prices[0]?.country,
        sku_id: plan?.id,
        trial: eligibleForFreeTrial,
        space_id: spaceData?.id
      };

      const res = await CreatePurchaseId(payload);

      dispatch(
        setSelectedSubscriptionInfo({
          // isUpgrade: !homeSecuritySubscriptionList[0]?.id,
          isUpgrade: false,
          isFreeTrial: isFreeTrial,
          planSelected: plan,
          purchaseId: res?.data?.purchase_id,
          deviceId
        })
      );

      navigate('/checkout-session');
    } catch (err) {
      console.log('purchases err', err);
    }
  };

  const findUserType = () => {
    let userType = '';
    if (isFreeTrial && subscriptionData?.length === 0) {
      userType = 'free_trial_eligible_user';
    } else if (isFreeTrial && subscriptionData?.length !== 0) {
      userType = 'free_trial_user';
    } else if (!isFreeTrial && subscriptionData?.length === 0) {
      userType = 'free_user';
    } else {
      userType = 'subscribed_user';
    }
    return userType;
  };

  useEffect(() => {
    analyticsService.trackEvent(AppEvent.SCREEN_VIEW_PLAN_LIST, {
      user_type: findUserType()
    });
    // console.log('SCREEN_VIEW_PLAN_LIST',findUserType());
  }, []);

  const handlePlanSelection = (el: TPlanList) => {
    createPurchaseId(el);
  };

  if (isLoading) {
    return <PlanPageLoader canBuyPlan />;
  }

  // if (error) {
  //   return (
  //     <Stack alignItems="center" marginY={8}>
  //       <Typography color="error">
  //         {error ||
  //           'There was an error loading the plans. Please reload the page.'}
  //       </Typography>
  //     </Stack>
  //   );
  // }

  if (
    !isLoading &&
    isPlanFiltered &&
    (allPlans?.length === 0 || selectedCyclesPlans?.length === 0)
  ) {
    console.log('selectedCyclesPlans?.length', selectedCyclesPlans?.length);
    console.log('allPlans?.length', allPlans?.length);
    return (
      <Stack pt={2.5}>
        <Typography
          variant="label2"
          sx={{ textTransform: 'capitalize', textAlign: 'center' }}
        >
          {t('noPlansAvailable')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack>
      {/* If user is in preview then show this  */}
      {isFreeTrial &&
        homeSecuritySubscriptionList[0]?.subscription_type ===
          SUBSCRIPTION_TYPE.PREVIEW && (
          <Typography
            variant="title1"
            sx={{ textTransform: 'capitalize', textAlign: 'center' }}
          >
            {enablePromoDiscount ? (
              <Trans
                t={t}
                i18nKey="discountPromoHeader"
                values={{
                  discount: '30%',
                  endDate: 'July 6th'
                }}
                components={{
                  strong: (
                    <span
                      style={{
                        color: palette.secondary.main
                      }}
                    />
                  )
                }}
              />
            ) : (
              t('afterPreviewBuyPlanHeader')
            )}
          </Typography>
        )}
      {/* If user is is eligible for free trial and its not upgrade and not preview plan then show this */}
      {isFreeTrial &&
        homeSecuritySubscriptionList[0]?.subscription_type !==
          SUBSCRIPTION_TYPE.PREVIEW && (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {isDefaultFreeTrialDays ? (
              <>
                <Typography variant="label2" sx={{ textAlign: 'center' }}>
                  {/* {`Choose your ${FREE_TRIAL_DURATION} days Free Trial plan`} */}
                  {t('chooseTrialPlanDays')}
                </Typography>
                <Typography
                  variant="title1"
                  sx={{
                    textTransform: 'capitalize',
                    textAlign: 'center',
                    color: 'secondary.main'
                  }}
                >
                  {/* {`Choose your ${FREE_TRIAL_DURATION} days Free Trial plan`} */}
                  {t('freeTrialDays', {
                    duration: freeTrialDays
                  })}
                </Typography>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <Typography variant="label2" sx={{ textAlign: 'center' }}>
                  {enablePromoDiscount
                    ? t('holidayOffer')
                    : t('exclusiveOffer')}
                </Typography>

                <Typography
                  variant="title1"
                  sx={{
                    textAlign: 'center'
                  }}
                >
                  {enablePromoDiscount ? (
                    <Trans
                      t={t}
                      i18nKey="offerGetFreeTrial"
                      values={{
                        duration: freeTrialDays,
                        discount: '30%',
                        festival: 'July 4th weekend'
                      }}
                      components={{
                        strong: (
                          <span
                            style={{
                              color: palette.secondary.main
                            }}
                          />
                        )
                      }}
                    />
                  ) : (
                    <Trans
                      t={t}
                      i18nKey="getFreeTrial"
                      values={{
                        duration: freeTrialDays
                      }}
                      components={{
                        strong: (
                          <span
                            style={{
                              color: palette.secondary.main
                            }}
                          />
                        )
                      }}
                    />
                  )}
                </Typography>
              </Box>
            )}
          </>
        )}
      {/* If user is not eligible for free trial and its not upgrade => Means user buy an actual plan */}
      {!isFreeTrial && (
        <Typography
          variant="label2"
          sx={{ textTransform: 'capitalize', textAlign: 'center' }}
        >
          {enablePromoDiscount ? (
            <Trans
              t={t}
              i18nKey="discountPromoHeader"
              values={{
                discount: '30%',
                endDate: 'July 6th'
              }}
              components={{
                strong: (
                  <span
                    style={{
                      color: palette.secondary.main
                    }}
                  />
                )
              }}
            />
          ) : (
            t('afterPreviewBuyPlanHeader')
          )}
        </Typography>
      )}

      {!isLoading && allPlans.length > 0 && selectedCyclesPlans.length > 0 && (
        <PlanComparisonComponent
          allPlans={allPlans}
          plansData={selectedCyclesPlans}
          allowSelection
          onPlanSelection={handlePlanSelection}
          isFreeTrial={isFreeTrial}
          isUpgrade={false}
          onChange={filterPlansForCycle}
        />
      )}
    </Stack>
  );
}

export default BuySubscription;
